/**
 * @module emerge
 * @since 2.0.0
 */

import { env } from '../../environment';

/**
 * Triggers a callback when an element "emerges", i.e. becomes visible on it's own or by inheritance
 * @param  {HTMLElement} element The element to "watch".
 * @return {Promise<HTMLElement>} Promise resolves with given HTML element as an argument
 *
 * @example
 * emerge(document.querySelector('.my-content'))
 *   .then(() => { console.log('My content is visible'); })
 *   .catch((error) => { throw error; });
 */
export const emerge = (element) => new Promise((resolve, reject) => {
    if (!env.browser) {
        reject('emerge can not be triggered from a non browser environment');
    }

    const hiddenContainer = getTraversedHidden(element);

    if (hiddenContainer === null) {
        resolve(element);
        return;
    }

    if (typeof MutationObserver === 'undefined') {
        // Unsupported. Pretend like you've emerged

        resolve(element);
        return;
    }

    const observer = new MutationObserver(() => {
        if (getTraversedHidden(element) === null) {
            resolve(element);
            observer.disconnect();
        }
    });

    observer.observe(hiddenContainer, { attributes: true });
});

/**
 * Retrieve the first element which is set to display:none. Returns null when none are found
 * @private
 * @param  {HTMLElement} element
 * @return {HTMLElement|null}
 */
function getTraversedHidden(element) {
    const { width, height } = element.getBoundingClientRect();

    if (width !== 0 || height !== 0) {
        return null;
    }

    while (element) {
        if (element === document.body) {

            // Break the loop two iterations early
            return null;
        }

        if (window.getComputedStyle(element).display === 'none') {

            // Break iteration with retrieve "hidden" element
            return element;
        }

        element = element.parentElement;
    }

    return element;
}
